import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQuoteLeft, faQuoteRight, faStar } from '@fortawesome/free-solid-svg-icons';

const CardContainer = styled(motion.div)`
  background-color: white;
  border-radius: var(--border-radius-md);
  box-shadow: var(--shadow-md);
  padding: var(--spacing-lg);
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: all 0.5s ease;
  position: relative;
  
  &:hover {
    transform: translateY(-8px) scale(1.02);
    box-shadow: var(--shadow-lg);
    
    &:before {
      opacity: 1;
    }
  }
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 4px;
    background: linear-gradient(90deg, var(--color-primary) 0%, var(--color-secondary) 100%);
    opacity: 0;
    transition: opacity 0.5s ease;
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: var(--spacing-md);
`;

const AvatarWrapper = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  overflow: hidden;
  margin-right: var(--spacing-md);
  border: 2px solid var(--color-primary-light);
  flex-shrink: 0;
  transition: all 0.3s ease;
  transform-origin: center;
  
  ${CardContainer}:hover & {
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

const Avatar = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const UserInfo = styled.div`
  flex-grow: 1;
`;

const UserName = styled.h4`
  margin: 0 0 var(--spacing-xs);
  font-size: 1.1rem;
  color: var(--color-text);
`;

const UserLocation = styled.p`
  margin: 0;
  font-size: 0.85rem;
  color: var(--color-text-light);
`;

const Rating = styled.div`
  display: flex;
  color: #FFC107;
  font-size: 0.9rem;
  margin-top: var(--spacing-xs);
`;

const QuoteIcon = styled.div`
  color: var(--color-primary-lightest);
  font-size: 1.5rem;
  line-height: 1;
  transition: all 0.3s ease;
  
  &.left {
    margin-right: var(--spacing-xs);
    align-self: flex-start;
    
    ${CardContainer}:hover & {
      transform: translateY(-2px) scale(1.1);
      color: var(--color-primary-light);
    }
  }
  
  &.right {
    margin-left: var(--spacing-xs);
    align-self: flex-end;
    
    ${CardContainer}:hover & {
      transform: translateY(2px) scale(1.1);
      color: var(--color-primary-light);
    }
  }
`;

const ReviewText = styled.div`
  font-size: 0.95rem;
  line-height: 1.6;
  color: var(--color-text);
  margin: 0;
  flex-grow: 1;
  position: relative;
  padding: 0 var(--spacing-sm);
  display: flex;
`;

const ReviewDate = styled.div`
  font-size: 0.8rem;
  color: var(--color-text-light);
  margin-top: var(--spacing-md);
  text-align: right;
`;

// Функция для рендеринга звезд рейтинга
const renderStars = (count = 5) => {
  return Array(5).fill(0).map((_, index) => (
    <FontAwesomeIcon 
      key={index} 
      icon={faStar} 
      style={{ opacity: index < count ? 1 : 0.3 }}
    />
  ));
};

// Функция для форматирования случайной даты в пределах последних 3 месяцев
const formatRandomDate = () => {
  const now = new Date();
  const threeMonthsAgo = new Date(now);
  threeMonthsAgo.setMonth(now.getMonth() - 3);
  
  const randomTimestamp = threeMonthsAgo.getTime() + Math.random() * (now.getTime() - threeMonthsAgo.getTime());
  const randomDate = new Date(randomTimestamp);
  
  return randomDate.toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
};

const ReviewCard = ({ review }) => {
  const { name, location, text, image } = review;
  
  // Генерируем случайное количество звезд (от 4 до 5)
  const randomRating = Math.floor(Math.random() * 2) + 4;
  
  return (
    <CardContainer
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ 
        duration: 0.5,
        type: "spring",
        stiffness: 100
      }}
      whileHover={{ scale: 1.02 }}
    >
      <CardHeader>
        <AvatarWrapper>
          <Avatar src={image} alt={name} />
        </AvatarWrapper>
        <UserInfo>
          <UserName>{name}</UserName>
          <UserLocation>{location}</UserLocation>
          <Rating>
            {renderStars(randomRating)}
          </Rating>
        </UserInfo>
      </CardHeader>
      
      <ReviewText>
        <QuoteIcon className="left">
          <FontAwesomeIcon icon={faQuoteLeft} />
        </QuoteIcon>
        <p>{text}</p>
        <QuoteIcon className="right">
          <FontAwesomeIcon icon={faQuoteRight} />
        </QuoteIcon>
      </ReviewText>
      
      <ReviewDate>{formatRandomDate()}</ReviewDate>
    </CardContainer>
  );
};

export default ReviewCard; 